import { useMutation } from '@apollo/client';
import { Dialog, DialogContent, DialogTitle, MenuItem, Select, TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { gql } from '__generated__';
import dayjs, { Dayjs } from 'dayjs';
import { CSSProperties, FC, useState } from 'react';
import { useNavigate } from 'react-router';

import Button from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import LoadingButton from 'components/LoadingButton';

import theme from './theme.module.scss';

// Matches FormPanel theme.module.scss
const label: CSSProperties = {
  fontSize: '0.8rem',
  color: '#696969',
  marginBottom: '0.25rem',
  marginTop: 0,
};
const errorText: CSSProperties = {
  fontSize: '0.75rem',
  color: '#d32f2f',
  marginLeft: '1rem',
  marginTop: '2px',
};
const fieldValue: CSSProperties = { fontWeight: 500, fontSize: '1rem', marginBottom: '0.5rem' };
const buttonContainer: CSSProperties = {
  display: 'inline-flex',
  flexFlow: 'row nowrap',
  margin: '0.5rem 0',
};

const EXPIRY_DURATION_OPTIONS = [
  { label: '3 Months', value: 'THREE_MONTHS' },
  { label: '6 Months', value: 'SIX_MONTHS' },
  { label: '1 Year', value: 'ONE_YEAR' },
  { label: '2 Years', value: 'TWO_YEARS' },
  { label: '3 Years', value: 'THREE_YEARS' },
  { label: '4 Years', value: 'FOUR_YEARS' },
  { label: '5 Years', value: 'FIVE_YEARS' },
  { label: 'Custom', value: 'CUSTOM' },
];

function computeExpiryDate(issueDate: Dayjs, duration: string): string {
  switch (duration) {
    case 'THREE_MONTHS':
      return issueDate.add(3, 'month').toISOString();
    case 'SIX_MONTHS':
      return issueDate.add(6, 'month').toISOString();
    case 'ONE_YEAR':
      return issueDate.add(1, 'year').toISOString();
    case 'TWO_YEARS':
      return issueDate.add(2, 'year').toISOString();
    case 'THREE_YEARS':
      return issueDate.add(3, 'year').toISOString();
    case 'FOUR_YEARS':
      return issueDate.add(4, 'year').toISOString();
    case 'FIVE_YEARS':
      return issueDate.add(5, 'year').toISOString();
    default:
      return issueDate.add(5, 'year').toISOString();
  }
}

const GENERATE_IDENTITY_CARDS_MUTATION = gql(`
  mutation GenerateIdentityCards(
    $departmentId: ID!
    $issueDate: String!
    $expiryDate: String!
  ) {
    generateIdentityCardsForDepartment(
      departmentId: $departmentId
      issueDate: $issueDate
      expiryDate: $expiryDate
    )
  }
`);

const GenerateIdentityCardsForDepartment: FC<{ departmentId: string }> = ({ departmentId }) => {
  const [showDialog, toggleDialog] = useState(false);
  const navigate = useNavigate();

  const [issueDate, setIssueDate] = useState<Dayjs>(dayjs());
  const [expiryDuration, setExpiryDuration] = useState('FIVE_YEARS');
  const [customExpiryDate, setCustomExpiryDate] = useState<Dayjs | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [generateIdentityCards, { loading, error }] = useMutation(
    GENERATE_IDENTITY_CARDS_MUTATION,
    {
      refetchQueries: ['DepartmentIdentityCards'],
      onCompleted() {
        navigate(`/departments/${departmentId}/identity-cards`);
      },
    }
  );

  function handleClose() {
    toggleDialog(false);
    setIssueDate(dayjs());
    setExpiryDuration('FIVE_YEARS');
    setCustomExpiryDate(null);
    setErrors({});
  }

  function handleSubmit() {
    const newErrors: Record<string, string> = {};
    if (!issueDate) newErrors.issueDate = 'This field is required.';
    if (expiryDuration === 'CUSTOM' && !customExpiryDate)
      newErrors.customExpiryDate = 'This field is required.';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const expiryDate =
      expiryDuration === 'CUSTOM'
        ? customExpiryDate!.toISOString()
        : computeExpiryDate(issueDate, expiryDuration);

    generateIdentityCards({
      variables: { departmentId, issueDate: issueDate.toISOString(), expiryDate },
    }).then(() => handleClose());
  }

  return (
    <>
      <Button variant="contained" onClick={() => toggleDialog(true)}>
        Generate Identity Cards For All Active Users
      </Button>
      <Dialog open={showDialog} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Generate Identity Cards</DialogTitle>
        <DialogContent sx={{ minHeight: '50vh' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div>
              <p style={label}>Issue Date</p>
              <div style={fieldValue}>
                <DatePicker
                  value={issueDate}
                  onChange={val => {
                    setIssueDate(val!);
                    setErrors(prev => ({ ...prev, issueDate: '' }));
                  }}
                  renderInput={params => (
                    <TextField fullWidth size="small" {...params} error={!!errors.issueDate} />
                  )}
                  inputFormat="DD/MM/YYYY"
                />
                {errors.issueDate && <p style={errorText}>{errors.issueDate}</p>}
              </div>
            </div>

            <div>
              <p style={label}>Expiry Duration</p>
              <div style={fieldValue}>
                <Select
                  fullWidth
                  size="small"
                  value={expiryDuration}
                  onChange={ev => {
                    setExpiryDuration(ev.target.value as string);
                    setErrors(prev => ({ ...prev, customExpiryDate: '' }));
                  }}
                >
                  {EXPIRY_DURATION_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>

            {expiryDuration === 'CUSTOM' && (
              <div>
                <p style={label}>Custom Expiry Date</p>
                <div style={fieldValue}>
                  <DatePicker
                    value={customExpiryDate}
                    onChange={val => {
                      setCustomExpiryDate(val);
                      if (val) setErrors(prev => ({ ...prev, customExpiryDate: '' }));
                    }}
                    renderInput={params => (
                      <TextField
                        fullWidth
                        size="small"
                        {...params}
                        error={!!errors.customExpiryDate}
                      />
                    )}
                    inputFormat="DD/MM/YYYY"
                  />
                  {errors.customExpiryDate && <p style={errorText}>{errors.customExpiryDate}</p>}
                </div>
              </div>
            )}

            {error && <ErrorMessage error={error} type="alert" />}

            <div style={buttonContainer}>
              <LoadingButton
                loading={loading}
                className={theme.submitButton}
                onClick={handleSubmit}
              >
                Generate
              </LoadingButton>
              <Button onClick={handleClose}>Cancel</Button>
            </div>
          </LocalizationProvider>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GenerateIdentityCardsForDepartment;
